import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter } from 'react-router';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { ErrorBoundary } from './components/error/ErrorBoundary';
import { HomepageRouter } from './components/layout/HomepageRouter';
import { Layout } from './components/layout/Layout';
import PosthogProvider from './components/layout/PosthogProvider';
import { AdminLayout } from './pages/Admin/components/AdminLayout';
import { ConsultantClientOrganisationsPage } from './pages/Consultant/ConsultantClientOrganisationsPage';
import { ConsultantLayout } from './pages/Consultant/ConsultantLayout';
import { ConsultantTriagePage } from './pages/Consultant/ConsultantTriagePage';
import { ConsultantUpdatesPage } from './pages/Consultant/ConsultantUpdatesPage';
import { FilePreviewPage } from './pages/Admin/components/sharedKnowledgePage/FilePreview';
import { AdminInstitutionsPage } from './pages/Admin/pages/RegulatoryWatch/InstitutionsPage';
import { AdminReferencesPage } from './pages/Admin/pages/RegulatoryWatch/ReferencesPage';
import { AdminScrapePage } from './pages/Admin/pages/RegulatoryWatch/ScrapePage';
import { AdminSourcesPage } from './pages/Admin/pages/RegulatoryWatch/SourcesPage';
import { AdminUpdatesPage } from './pages/Admin/pages/RegulatoryWatch/UpdatesPage';
import { AdminCountriesPage } from './pages/Admin/pages/RegulatoryWatch/CountriesPage';
import { AdminDiscardedItemsPage } from './pages/Admin/pages/RegulatoryWatch/DiscardedItemsPage';
import { AdminEditSourcePage } from './pages/Admin/pages/RegulatoryWatch/EditSourcePage';
import { AdminEditReferencePage } from './pages/Admin/pages/RegulatoryWatch/EditReferencePage';
import { AdminEditUpdatePage } from './pages/Admin/pages/RegulatoryWatch/EditUpdatePage';
import { AdminSharedKnowledgePage } from './pages/Admin/pages/SharedKnowledgePage';
import { ApiClientsPage } from './pages/Admin/pages/ApiClientsPage';
import { ConsultantsPage } from './pages/Admin/pages/ConsultantsPage';
import { UsersPage } from './pages/Admin/pages/UsersPage';
import { AdminWorkspacesPage } from './pages/Admin/pages/WorkspacesPage';
import { AskPage } from './pages/Ask';
import { LoginRequired } from './pages/Authentication/LoginRequired';
import { WorkspaceSetupPage } from './pages/Authentication/WorkspaceSetup';
import { AgentChat } from './pages/Chat';
import { HistoryPage } from './pages/ChatsHistory';
import { LiteratureReviewsPage } from './pages/LiteratureReviews';
import { LiteratureReviewLayout } from './pages/LiteratureReviews/literatureReview';
import { LiteratureReviewAbstractScreeningPage } from './pages/LiteratureReviews/literatureReview/AbstractScreening';
import { FullTextScreeningPage } from './pages/LiteratureReviews/literatureReview/FullTextScreening';
import { LiteratureReviewSearchStepPage } from './pages/LiteratureReviews/literatureReview/SearchStep';
import { ProjectsPage } from './pages/Projects';
import { ProjectPage } from './pages/Projects/Project';
import { RegulatoryWatchLayout } from './pages/RegulatoryWatch';
import { AllDocumentsPage } from './pages/RegulatoryWatch/AllDocuments';
import { ReferenceDetailPage } from './pages/RegulatoryWatch/ReferenceDetail';
import { UpdateDetailPage } from './pages/RegulatoryWatch/UpdateDetail';
import { regulatoryWatchListLoader } from './pages/RegulatoryWatch/regulatoryWatchListRouteLoader';
import {
  AllUpdatesPage,
  AuditTablePage,
  NewsfeedPage,
} from './pages/RegulatoryWatch/UpdatesFeed';
import { SettingsLayout } from './pages/Settings';
import { CompanyProfileTab } from './pages/Settings/components/CompanyProfileTab';
import { IntegrationsTab } from './pages/Settings/components/IntegrationsTab';
import { MyAccountTab } from './pages/Settings/components/MyAccountTab';
import { SubscriptionsTab } from './pages/Settings/components/SubscriptionsTab';
import { McpPage } from './pages/Mcp';
import { SourcesPage } from './pages/Sources';
import { StandardsChatPage, StandardsPage } from './pages/Standards';
import { TableViewsPage } from './pages/TableViews';
import { TableViewPage } from './pages/TableViews/TableView';
import { WorkspacesPage } from './pages/Workspaces';
import {
  adminPaths,
  adminRegulatoryWatchPaths,
  chatPaths,
  consultantPaths,
  literatureReviewPaths,
  paths,
  projectPaths,
  regulatoryWatchPaths,
  settingsPaths,
  standardsPaths,
  tableViewPaths,
  WORKSPACE_PATH,
} from './paths';

const queryClient = new QueryClient();

const AskIndexRedirect = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  return (
    <Navigate
      to={`/workspace/${workspaceUname}/${paths.ask}/${chatPaths.askNew}`}
      replace
    />
  );
};

const ChatsIndexRedirect = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  return (
    <Navigate
      to={`/workspace/${workspaceUname}/${paths.ask}/${chatPaths.askHistory}`}
      replace
    />
  );
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const isDevBypass =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

if (!PUBLISHABLE_KEY && !isDevBypass) {
  throw new Error('Missing Publishable Key');
}

const Root = () => {
  return (
    <div className="relative scroll-smooth overflow-hidden">
      <QueryClientProvider client={queryClient}>
        <ClerkProvider publishableKey={PUBLISHABLE_KEY ?? ''}>
          <PosthogProvider>
            <Outlet />
          </PosthogProvider>
        </ClerkProvider>
      </QueryClientProvider>
    </div>
  );
};

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Root,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/sign-up',
        element: <Navigate to="/sign-in" replace />,
      },
      {
        path: '/onboarding',
        element: <Navigate to="/sign-in" replace />,
      },
      {
        path: '/',
        Component: LoginRequired,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: '/workspace',
            Component: WorkspaceSetupPage,
            errorElement: <ErrorBoundary />,
          },
          {
            path: '/consultant/:orgId',
            Component: ConsultantLayout,
            children: [
              {
                index: true,
                element: <Navigate to={consultantPaths.updates} replace />,
              },
              {
                path: consultantPaths.updates,
                Component: ConsultantUpdatesPage,
              },
              {
                path: consultantPaths.triage,
                Component: ConsultantTriagePage,
              },
              {
                path: consultantPaths.clientOrganisations,
                Component: ConsultantClientOrganisationsPage,
              },
            ],
          },
          {
            path: '/admin',
            Component: AdminLayout,
            children: [
              {
                index: true,
                element: <Navigate to={adminPaths.users} replace />,
              },
              {
                path: adminPaths.users,
                Component: UsersPage,
              },
              {
                path: adminPaths.workspaces,
                Component: AdminWorkspacesPage,
              },
              {
                path: adminPaths.sharedKnowledge,
                Component: AdminSharedKnowledgePage,
              },
              {
                path: adminPaths.regulatoryWatch,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={adminRegulatoryWatchPaths.sources}
                        replace
                      />
                    ),
                  },
                  {
                    path: adminRegulatoryWatchPaths.sources,
                    Component: AdminSourcesPage,
                  },
                  {
                    path: `${adminRegulatoryWatchPaths.sources}/${adminRegulatoryWatchPaths.source_id_param}`,
                    Component: AdminEditSourcePage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.references,
                    Component: AdminReferencesPage,
                  },
                  {
                    path: `${adminRegulatoryWatchPaths.references}/${adminRegulatoryWatchPaths.reference_id_param}`,
                    Component: AdminEditReferencePage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.updates,
                    Component: AdminUpdatesPage,
                  },
                  {
                    path: `${adminRegulatoryWatchPaths.updates}/${adminRegulatoryWatchPaths.update_id_param}`,
                    Component: AdminEditUpdatePage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.institutions,
                    Component: AdminInstitutionsPage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.countries,
                    Component: AdminCountriesPage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.scrape,
                    Component: AdminScrapePage,
                  },
                  {
                    path: adminRegulatoryWatchPaths.discardedItems,
                    Component: AdminDiscardedItemsPage,
                  },
                ],
              },
              {
                path: adminPaths.consultants,
                Component: ConsultantsPage,
              },
              {
                path: adminPaths.apiClients,
                Component: ApiClientsPage,
              },
              {
                path: `${adminPaths.files}/:file_id`,
                Component: FilePreviewPage,
              },
            ],
          },
          {
            path: '/',
            Component: WorkspacesPage,
          },
          {
            path: WORKSPACE_PATH,
            Component: Layout,
            errorElement: <ErrorBoundary />,
            children: [
              { index: true, Component: HomepageRouter },
              {
                path: paths.ask,
                children: [
                  {
                    index: true,
                    Component: AskIndexRedirect,
                  },
                  {
                    path: chatPaths.askNew,
                    Component: AskPage,
                  },
                  {
                    path: chatPaths.askHistory,
                    Component: HistoryPage,
                  },
                ],
              },
              {
                path: paths.chats,
                children: [
                  {
                    index: true,
                    Component: ChatsIndexRedirect,
                  },
                  {
                    path: chatPaths.chat_id_param,
                    Component: AgentChat,
                  },
                ],
              },
              {
                path: paths.projects,
                children: [
                  { index: true, Component: ProjectsPage },
                  {
                    path: projectPaths.project_id_param,
                    Component: ProjectPage,
                  },
                ],
              },
              {
                path: paths.literatureReviews,
                children: [
                  {
                    index: true,
                    Component: LiteratureReviewsPage,
                  },
                  {
                    path: literatureReviewPaths.literatureReview_id_param,
                    Component: LiteratureReviewLayout,
                    children: [
                      {
                        path: literatureReviewPaths.steps.search,
                        Component: LiteratureReviewSearchStepPage,
                      },
                      {
                        path: literatureReviewPaths.steps.abstractScreening,
                        Component: LiteratureReviewAbstractScreeningPage,
                      },
                      {
                        path: literatureReviewPaths.steps.fullTextScreening,
                        Component: FullTextScreeningPage,
                      },
                    ],
                  },
                ],
              },
              {
                path: paths.sources,
                Component: SourcesPage,
              },
              {
                path: paths.mcp,
                Component: McpPage,
              },
              {
                path: paths.tableViews,
                children: [
                  { index: true, Component: TableViewsPage },
                  {
                    path: tableViewPaths.tableView_id_param,
                    Component: TableViewPage,
                  },
                ],
              },
              {
                path: paths.regulatoryWatch,
                Component: RegulatoryWatchLayout,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={regulatoryWatchPaths.allDocuments}
                        replace
                      />
                    ),
                  },
                  {
                    // Legacy: preserve bookmarks to /regulatory-watch/feed
                    path: 'feed',
                    element: (
                      <Navigate
                        to={`../${regulatoryWatchPaths.newsfeed}`}
                        replace
                      />
                    ),
                  },
                  {
                    path: regulatoryWatchPaths.newsfeed,
                    loader: regulatoryWatchListLoader('newsfeed'),
                    Component: NewsfeedPage,
                  },
                  {
                    path: regulatoryWatchPaths.auditTable,
                    loader: regulatoryWatchListLoader('audit-table'),
                    Component: AuditTablePage,
                  },
                  {
                    path: regulatoryWatchPaths.allUpdates,
                    loader: regulatoryWatchListLoader('all-updates'),
                    Component: AllUpdatesPage,
                  },
                  {
                    path: regulatoryWatchPaths.allDocuments,
                    loader: regulatoryWatchListLoader('all-documents'),
                    Component: AllDocumentsPage,
                  },
                  {
                    path: `updates/${regulatoryWatchPaths.update_id_param}`,
                    Component: UpdateDetailPage,
                  },
                  {
                    path: `references/${regulatoryWatchPaths.reference_id_param}`,
                    Component: ReferenceDetailPage,
                  },
                ],
              },
              {
                path: paths.settings,
                Component: SettingsLayout,
                children: [
                  {
                    index: true,
                    element: <Navigate to={settingsPaths.myAccount} replace />,
                  },
                  {
                    path: settingsPaths.myAccount,
                    Component: MyAccountTab,
                  },
                  {
                    path: settingsPaths.companyProfile,
                    Component: CompanyProfileTab,
                  },
                  {
                    path: settingsPaths.subscriptions,
                    Component: SubscriptionsTab,
                  },
                  {
                    path: settingsPaths.integrations,
                    Component: IntegrationsTab,
                  },
                ],
              },
              {
                path: paths.standards,
                children: [
                  { index: true, Component: StandardsPage },
                  {
                    path: standardsPaths.chat_id_param,
                    Component: StandardsChatPage,
                  },
                ],
              },
              {
                path: '*',
                Component: HomepageRouter,
              },
            ],
          },
        ],
      },
    ],
  },
]);
